// Pure filter + sort helpers for the Admin → Tags Inventory page. Ported from
// trusstor_dashboard's `adminPanelTags.utils.ts`, adapted to d-orca's flat
// `IAdminTag` domain shape (status field is `status`, profession carries
// `tradeId`, battery is `batteryLevel`, timestamps are ISO strings).

import type { IAdminTag, IAdminTagProfession, TagStatus } from '@interfaces/IAdminTag';

export const UNASSIGNED_TRADE_ID = 'unassigned';

// Day thresholds for the last-seen filter buckets (mirrors trusstor).
export const ADMIN_PANEL_LAST_SEEN_DAYS = {
  OK_MAX_DAYS: 5,
  WARNING_MAX_DAYS: 10,
};

export type AssignedFilterKey = 'assigned' | 'unassigned';
export type LastSeenFilterKey = 'ok' | 'warning' | 'alert';
export type BatteryFilterKey = 'high' | 'medium' | 'low';
export type StatusFilterKey = Extract<TagStatus, 'Good' | 'Sleeping' | 'Storage' | 'NoReception'>;

export interface IAdminPanelFilters {
  assigned: Record<AssignedFilterKey, boolean>;
  lastSeen: Record<LastSeenFilterKey, boolean>;
  status: Record<StatusFilterKey, boolean>;
  battery: Record<BatteryFilterKey, boolean>;
  /** Selected profession tradeIds. Empty = no profession filter. */
  professions: string[];
  /** Selected management role keys (manager.role). Empty = no management filter. */
  management: string[];
}

export const getInitialFixedFilterState = (): IAdminPanelFilters => ({
  assigned: { assigned: false, unassigned: false },
  lastSeen: { ok: false, warning: false, alert: false },
  status: { Good: false, Sleeping: false, Storage: false, NoReception: false },
  battery: { high: false, medium: false, low: false },
  professions: [],
  management: [],
});

const anyTrue = (rec: Record<string, boolean>): boolean => Object.values(rec).some(Boolean);

export const isTagAssigned = (tag: IAdminTag): boolean => {
  if (tag.manager || tag.linkedUtility) return true;
  return !!tag.profession && tag.profession.tradeId !== UNASSIGNED_TRADE_ID;
};

export const getBatteryLevelNumber = (battery: string | null): number => {
  if (!battery) return -Infinity;
  return battery.includes('%') ? Number(battery.replace('%', '')) : Number(battery);
};

/** Whole days between `iso` and now. `tz` reserved for parity; UTC-day math is
 *  close enough for the OK/warning/alert buckets (5 / 10 day thresholds). */
export const getDaysAgo = (iso: string | null): number => {
  if (!iso) return Infinity;
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return Infinity;
  return Math.floor((Date.now() - then) / 86_400_000);
};

export const isCertificateExpired = (expirationDate: string | null, now = Date.now()): boolean => {
  if (!expirationDate) return false;
  const exp = new Date(expirationDate).getTime();
  if (Number.isNaN(exp)) return false;
  return exp <= now;
};

export const hasExpiredCertificate = (tag: IAdminTag): boolean =>
  tag.certificates.some((c) => isCertificateExpired(c.expirationDate));

// ─── Filtering ───────────────────────────────────────────────────────────────

const fitsAssigned = (f: IAdminPanelFilters, tag: IAdminTag): boolean => {
  if (!anyTrue(f.assigned)) return true;
  if (f.assigned.assigned && isTagAssigned(tag)) return true;
  if (f.assigned.unassigned && !isTagAssigned(tag)) return true;
  return false;
};

const fitsLastSeen = (f: IAdminPanelFilters, tag: IAdminTag): boolean => {
  if (!anyTrue(f.lastSeen)) return true;
  const days = getDaysAgo(tag.lastSeen);
  if (f.lastSeen.ok && days <= ADMIN_PANEL_LAST_SEEN_DAYS.OK_MAX_DAYS) return true;
  if (
    f.lastSeen.warning &&
    days > ADMIN_PANEL_LAST_SEEN_DAYS.OK_MAX_DAYS &&
    days <= ADMIN_PANEL_LAST_SEEN_DAYS.WARNING_MAX_DAYS
  )
    return true;
  if (f.lastSeen.alert && days > ADMIN_PANEL_LAST_SEEN_DAYS.WARNING_MAX_DAYS) return true;
  return false;
};

const fitsStatus = (f: IAdminPanelFilters, tag: IAdminTag): boolean => {
  if (!anyTrue(f.status)) return true;
  return tag.status in f.status && f.status[tag.status as StatusFilterKey];
};

const fitsBattery = (f: IAdminPanelFilters, tag: IAdminTag): boolean => {
  if (!anyTrue(f.battery)) return true;
  const lvl = getBatteryLevelNumber(tag.batteryLevel);
  if (f.battery.high && lvl > 60) return true;
  if (f.battery.medium && lvl > 30 && lvl <= 60) return true;
  if (f.battery.low && lvl >= 0 && lvl <= 30) return true;
  return false;
};

const fitsProfessions = (f: IAdminPanelFilters, tag: IAdminTag): boolean => {
  if (f.professions.length === 0) return true;
  return !!tag.profession?.id && f.professions.includes(tag.profession.id);
};

const fitsManagement = (f: IAdminPanelFilters, tag: IAdminTag): boolean => {
  if (f.management.length === 0) return true;
  return !!tag.manager?.role && f.management.includes(tag.manager.role);
};

export const filterTags = (tags: IAdminTag[], f: IAdminPanelFilters): IAdminTag[] =>
  tags.filter(
    (tag) =>
      fitsAssigned(f, tag) &&
      fitsLastSeen(f, tag) &&
      fitsStatus(f, tag) &&
      fitsBattery(f, tag) &&
      fitsProfessions(f, tag) &&
      fitsManagement(f, tag),
  );

export const filterTagsBySearch = (tags: IAdminTag[], search: string): IAdminTag[] => {
  const q = search.trim().toLowerCase();
  if (!q) return tags;
  return tags.filter(
    (tag) =>
      tag.fullName.toLowerCase().includes(q) ||
      (tag.tagNick?.toLowerCase().includes(q) ?? false) ||
      (tag.idNumber?.toLowerCase().includes(q) ?? false),
  );
};

// ─── Derived filter options (no extra endpoints — read from the tags) ──────────

export interface IFilterOption {
  id: string;
  label: string;
}

export const getManagementOptions = (tags: IAdminTag[]): IFilterOption[] => {
  const seen = new Set<string>();
  for (const tag of tags) {
    if (tag.manager?.role) seen.add(tag.manager.role);
  }
  return [...seen].map((role) => ({ id: role, label: role })).sort((a, b) => a.label.localeCompare(b.label));
};

export interface IProfessionFilterOption {
  /** Profession `_id` — the filter join key (matches `tag.profession.id`). */
  id: string;
  tradeId: string;
  specialty: string | null;
  contractor: string | null;
  backgroundColor: string | null;
  /** Number of tags in this profession (0 is kept — every profession shows). */
  count: number;
}

/** Profession filter rows from the full site professions list, one per
 *  profession (`_id`) — mirrors trusstor's `EntitiesWithValuesFilterSection`:
 *  professions that share a trade but differ by specialty/contractor are
 *  separate rows. Keeps zero-count professions and counts tags by profession
 *  `id`. Unsorted here — the caller sorts by count desc then label. */
export const getProfessionFilterOptions = (
  professions: ReadonlyArray<{
    _id: string;
    tradeId: string;
    specialty?: string | null;
    contractor?: string | null;
    backgroundColor?: string | null;
  }>,
  tags: IAdminTag[],
): IProfessionFilterOption[] => {
  const counts = new Map<string, number>();
  for (const tag of tags) {
    const id = tag.profession?.id;
    if (id) counts.set(id, (counts.get(id) ?? 0) + 1);
  }
  const byId = new Map<string, IProfessionFilterOption>();
  for (const p of professions) {
    if (!p._id || p.tradeId === UNASSIGNED_TRADE_ID || byId.has(p._id)) continue;
    byId.set(p._id, {
      id: p._id,
      tradeId: p.tradeId,
      specialty: p.specialty ?? null,
      contractor: p.contractor ?? null,
      backgroundColor: p.backgroundColor ?? null,
      count: counts.get(p._id) ?? 0,
    });
  }
  return [...byId.values()];
};

// ─── Sorting ───────────────────────────────────────────────────────────────

export const getLinkedSortIndex = (tag: IAdminTag): number =>
  tag.profession?.sortIndex ?? tag.manager?.sortIndex ?? tag.linkedUtility?.sortIndex ?? Number.MAX_SAFE_INTEGER;

export const getStatusSortOrder = (tag: IAdminTag): number => {
  if (tag.status === 'Good') return 0;
  if (tag.status === 'Sleeping') return 1;
  if (tag.status === 'Storage') return 2;
  return 3;
};

export const getCertificateSortOrder = (tag: IAdminTag): number => {
  if (tag.certificates.length === 0) return 0;
  if (hasExpiredCertificate(tag)) return 1;
  return 2;
};

export const getLinkedToLabel = (tag: IAdminTag): string => {
  if (tag.manager) return tag.manager.name || tag.manager.role || '';
  if (tag.linkedUtility) return tag.linkedUtility.name;
  if (tag.profession) return tag.profession.tradeName ?? tag.profession.tradeId;
  return '';
};

/**
 * Chip colour for a tag's profession, in the precedence Monitor and Scheduler
 * use: the site professions catalog (joined by profession `_id`, and already
 * carrying the backend-resolved per-project override) wins over the colour
 * embedded in the tag's SSE frame, which is only the profession's *base*
 * colour. The embedded colour survives as a fallback for professions absent
 * from the catalog; `undefined` lets TradePill apply its neutral fallback.
 *
 * Joining by `_id` and not `tradeId` is deliberate — two professions under one
 * trade can carry different colours.
 */
export const resolveTagProfessionColor = (
  profession: Pick<IAdminTagProfession, 'id' | 'backgroundColor'>,
  colorByProfessionId: ReadonlyMap<string, string | null>,
): string | undefined => {
  const fromCatalog = profession.id ? colorByProfessionId.get(profession.id) : null;
  return fromCatalog || profession.backgroundColor || undefined;
};
